import PropTypes from "prop-types";
import * as React from "react";
import { useDebounce, useProductTagsSearch } from "../../hooks";
import { Autocomplete } from "../../themeV2";

const DEFAULT_MAX_LENGTH = 45;
const SEARCH_DEBOUNCE_MS = 250;

function defaultLabelFor({ isCharacter, isFranchise }) {
  if (isCharacter) return "Add a character";
  if (isFranchise) return "Add a franchise";
  return "Add a new tag";
}

export default function TagSearch({
  clearOnBlur = false,
  isCharacter = false,
  isFranchise = false,
  label,
  maxLength = DEFAULT_MAX_LENGTH,
  onAddTag,
  onRemoveTag,
  tag = null,
}) {
  const inputRef = React.useRef(null);
  const tagRef = React.useRef(tag);
  const [inputValue, setInputValue] = React.useState(tag ?? "");
  const [query, setQuery] = React.useState("");
  const { tags } = useProductTagsSearch(query, { isCharacter, isFranchise });

  useDebounce(() => setQuery(inputValue.trim()), SEARCH_DEBOUNCE_MS, [
    inputValue,
  ]);

  React.useEffect(() => {
    if (tag !== tagRef.current) {
      tagRef.current = tag;
      setInputValue(tag ?? "");
    }
  }, [tag]);

  const commit = React.useCallback(
    (value) => {
      const trimmed = value.trim();
      return trimmed ? onAddTag(trimmed) : onRemoveTag();
    },
    [onAddTag, onRemoveTag],
  );

  const handleValueChange = React.useCallback(
    (newValue) => {
      commit(newValue ?? "");
      setInputValue(clearOnBlur ? "" : newValue ?? "");
      window.requestAnimationFrame(() => inputRef.current?.blur?.());
    },
    [clearOnBlur, commit],
  );

  /* A field that discards on blur is an "add another" field — its text is only
     ever committed by choosing something. The rest commit what was typed. */
  const handleBlur = React.useCallback(() => {
    if (clearOnBlur) return;
    commit(inputValue);
  }, [clearOnBlur, commit, inputValue]);

  const typed = inputValue.trim();
  const suggestions = tags.filter((t) => t !== typed);

  return (
    <Autocomplete.Root
      ref={inputRef}
      label={label ?? defaultLabelFor({ isCharacter, isFranchise })}
      value={tag}
      onValueChange={handleValueChange}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      /* The suggestions arrive already matched by the server. */
      autoComplete="none"
      inputProps={{ maxLength, onBlur: handleBlur }}
    >
      {/* First, so it is the auto-highlighted choice: Enter commits what was
          typed rather than the closest suggestion. */}
      {Boolean(typed) && (
        <Autocomplete.Item value={typed}>{`Use "${typed}"`}</Autocomplete.Item>
      )}

      {suggestions.map((suggestion) => (
        <Autocomplete.Item key={suggestion} value={suggestion}>
          {suggestion}
        </Autocomplete.Item>
      ))}

      <Autocomplete.Empty>Type to search tags</Autocomplete.Empty>
    </Autocomplete.Root>
  );
}

TagSearch.propTypes = {
  clearOnBlur: PropTypes.bool,
  isCharacter: PropTypes.bool,
  isFranchise: PropTypes.bool,
  label: PropTypes.string,
  maxLength: PropTypes.number,
  onAddTag: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func.isRequired,
  tag: PropTypes.string,
};
